// utils/permissions.js

// Check if user has a specific permission
export const hasPermission = (user, resource, action) => {
  if (!user) return false;
  
  // Super admin has all permissions
  if (user.role === 'super_admin') {
    return true;
  }

  // Get effective permissions
  let permissions = {};
  
  // Use custom permissions if enabled (even if empty - that means no permissions)
  if (user.useCustomPermissions) {
    permissions = user.customPermissions || {};
  } else {
    // Use default role permissions
    permissions = getDefaultRolePermissions(user.role);
  }

  const resourcePermissions = permissions[resource] || [];
  return resourcePermissions.includes(action);
};

// Check if user has any permission for a resource
export const hasAnyPermission = (user, resource) => {
  if (!user) return false;
  
  // Super admin has all permissions
  if (user.role === 'super_admin') {
    return true;
  }

  let permissions = {};
  
  // Use custom permissions if enabled (even if empty - that means no permissions)
  if (user.useCustomPermissions) {
    permissions = user.customPermissions || {};
  } else {
    permissions = getDefaultRolePermissions(user.role);
  }

  const resourcePermissions = permissions[resource] || [];
  return resourcePermissions.length > 0;
};

// Get default permissions for a role (client-side fallback)
// MUST match backend/src/config/roles.js
export const getDefaultRolePermissions = (role) => {
  const defaults = {
    user: {
      dashboard: ['read'],
      events: ['read'],
      documents: ['read'],
    },
    staff: {
      dashboard: ['read'],
      events: ['read', 'create', 'update'],
      jathas: ['read'],
      program_types: ['read'],
      sikh_holidays: ['read'],
      mainboard: ['read', 'create', 'update'],
      scrollboard: ['read', 'create', 'update'],
      slideshow: ['read', 'create', 'update'],
      notices: ['read', 'create', 'update'],
      // Staff can view finances but not modify
      expenses: ['read'],
      golak: ['read'],
      donations: ['read'],
      // Staff can read and upload documents
      documents: ['read', 'create'],
      // Staff can view reports
      reports: ['read'],
      // Staff has no backup access
    },
    admin: {
      dashboard: ['read', 'create', 'update', 'delete'],
      events: ['read', 'create', 'update', 'delete'],
      jathas: ['read', 'create', 'update', 'delete'],
      program_types: ['read', 'create', 'update', 'delete'],
      sikh_holidays: ['read', 'create', 'update', 'delete'],
      mainboard: ['read', 'create', 'update', 'delete'],
      scrollboard: ['read', 'create', 'update', 'delete'],
      slideshow: ['read', 'create', 'update', 'delete'],
      notices: ['read', 'create', 'update', 'delete'],
      users: ['read'],
      // Admin has full finance access
      expenses: ['read', 'create', 'update', 'delete'],
      golak: ['read', 'create', 'update', 'delete'],
      donations: ['read', 'create', 'update', 'delete'],
      // Admin has full document access
      documents: ['read', 'create', 'update', 'delete'],
      // Admin has full reports access
      reports: ['read', 'create', 'update', 'delete'],
      // Admin has full backup access
      backups: ['read', 'create', 'update', 'delete'],
    },
    super_admin: {
      // Super admin has all permissions (handled separately)
    },
  };

  // Return default for role, or basic dashboard access as fallback
  return defaults[role] || { dashboard: ['read'] };
};

// Filter navigation items based on user permissions
export const filterNavigationByPermissions = (navItems, user) => {
  if (!user) {
    console.log('[Permissions] No user, returning empty nav');
    return [];
  }
  
  // Super admin sees everything
  if (user.role === 'super_admin') {
    return navItems;
  }

  // Debug: log user permissions
  console.log('[Permissions] User:', user.name, 'Role:', user.role);
  console.log('[Permissions] useCustomPermissions:', user.useCustomPermissions);
  console.log('[Permissions] customPermissions:', user.customPermissions);

  return navItems
    .map(group => {
      // Filter submenu items
      const filteredSubmenu = group.submenu.filter(item => {
        // If no permission required, show item
        if (!item.permission) return true;
        
        const { resource, action } = item.permission;
        const hasPerm = hasPermission(user, resource, action);
        console.log(`[Permissions] Check ${resource}.${action}: ${hasPerm}`);
        return hasPerm;
      });

      // Only include group if it has visible items
      if (filteredSubmenu.length === 0) return null;

      return {
        ...group,
        submenu: filteredSubmenu,
      };
    })
    .filter(Boolean); // Remove null groups
};

// Get user's effective permissions (for display)
export const getUserPermissions = (user) => {
  if (!user) return {};
  
  if (user.role === 'super_admin') {
    return { _isSuperAdmin: true };
  }

  // Use custom permissions if enabled (even if empty)
  if (user.useCustomPermissions) {
    return user.customPermissions || {};
  }

  return getDefaultRolePermissions(user.role);
};
